import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, 
  Search, 
  Plus, 
  Users, 
  BookOpen,
  Star,
  MapPin,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import TuitionTeacherRegistration from '../components/TuitionTeacherRegistration';
import TeacherSearch from '../components/TeacherSearch';
import TeacherBatch from '../components/TeacherBatch';
import TeacherProfile from '../components/TeacherProfile';
import { useRealtime } from '../contexts/RealtimeContext';
import { teacherHiringService, tuitionTeacherService, demoBookingService } from '../firebase/tuition';
import { toast } from 'react-hot-toast';

const TuitionTeachers = () => {
  const { currentUser: user } = useRealtime();
  const [activeTab, setActiveTab] = useState('search');
  const [showRegistration, setShowRegistration] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const tabs = [
    { id: 'search', label: 'Find Teachers', icon: Search },
    { id: 'my_demo_bookings', label: 'Demo Bookings', icon: Calendar },
    { id: 'my_teachers', label: 'My Teachers', icon: Users },
    { id: 'my_batches', label: 'My Batches', icon: BookOpen }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 py-4 sm:py-6">
            <div className="flex items-center">
              <GraduationCap size={24} className="sm:w-8 sm:h-8 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Tuition Teachers</h1>
                <p className="text-xs sm:text-base text-gray-600">Find and connect with qualified tutors</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              {user && (
                <button
                  onClick={() => setShowRegistration(true)}
                  className="flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={16} className="sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Register as Teacher</span>
                  <span className="sm:hidden">Register</span>
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto scrollbar-hide -mx-3 sm:mx-0 px-3 sm:px-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={16} className="sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'search' && <TeacherSearch />}
        {activeTab === 'my_demo_bookings' && <MyDemoBookingsTab onSelectTeacher={setSelectedTeacher} />}
        {activeTab === 'my_teachers' && <MyTeachersTab onSelectBatch={setSelectedBatch} onSelectTeacher={setSelectedTeacher} />}
        {activeTab === 'my_batches' && <MyBatchesTab onSelectBatch={setSelectedBatch} />}
      </div>

      {/* Modals */}
      {showRegistration && (
        <TuitionTeacherRegistration onClose={() => setShowRegistration(false)} />
      )}

      {selectedBatch && (
        <TeacherBatch
          batchId={selectedBatch}
          onClose={() => setSelectedBatch(null)}
        />
      )}

      {selectedTeacher && (
        <TeacherProfile
          teacherId={selectedTeacher}
          onClose={() => setSelectedTeacher(null)}
        />
      )}
    </div>
  );
};

// My Demo Bookings Tab Component
const MyDemoBookingsTab = ({ onSelectTeacher }) => {
  const { currentUser: user } = useRealtime();
  const [demoBookings, setDemoBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDemoBookings = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const bookings = await demoBookingService.getStudentDemoBookings(user.uid);
      
      // Fetch teacher details for each booking
      const bookingsWithTeachers = [];
      for (const booking of bookings) {
        try {
          const teacher = await tuitionTeacherService.getTeacher(booking.teacherId);
          bookingsWithTeachers.push({
            ...booking,
            teacher: teacher
          });
        } catch (error) {
          bookingsWithTeachers.push({
            ...booking,
            teacher: null
          });
        }
      }
      
      setDemoBookings(bookingsWithTeachers);
    } catch (error) {
      toast.error('Failed to load demo bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDemoBookings();
  }, [user]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="flex items-center px-2 sm:px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">
            <CheckCircle size={12} className="mr-1" />
            Confirmed
          </span>
        );
      case 'cancelled':
        return (
          <span className="flex items-center px-2 sm:px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full">
            <XCircle size={12} className="mr-1" />
            Cancelled
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center px-2 sm:px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
            <CheckCircle size={12} className="mr-1" />
            Completed
          </span>
        );
      default:
        return (
          <span className="flex items-center px-2 sm:px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
            <AlertCircle size={12} className="mr-1" />
            Pending
          </span>
        );
    }
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return 'Not specified';
    return `${new Date(date).toLocaleDateString()} at ${time}`;
  };

  if (loading) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-3 sm:mb-4"></div>
        <p className="text-sm sm:text-base text-gray-600">Loading your demo bookings...</p>
      </div>
    );
  }

  if (demoBookings.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <Calendar size={48} className="sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
        <h3 className="text-base sm:text-xl font-semibold text-gray-900 mb-2">No demo bookings yet</h3>
        <p className="text-xs sm:text-base text-gray-600 mb-4 px-4">
          Book a demo class with a teacher to get started
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">My Demo Bookings</h2>
        <button
          onClick={loadDemoBookings}
          disabled={loading}
          className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="space-y-4">
        {demoBookings.map((booking) => (
          <div key={booking.id} className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
              <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                {booking.teacher?.photoUrl ? (
                  <img
                    src={booking.teacher.photoUrl}
                    alt={booking.teacher.name}
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <GraduationCap size={20} className="sm:w-6 sm:h-6 text-gray-400" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                    {booking.teacher?.name || 'Teacher'}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600 mb-2">
                    <div className="flex items-center">
                      <BookOpen size={14} className="mr-1" />
                      <span>{booking.subject}</span>
                    </div>
                    {booking.teacher && (
                      <div className="flex items-center">
                        <MapPin size={14} className="mr-1" />
                        <span className="truncate">{booking.teacher.city}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Booked on: {booking.createdAt ? new Date(booking.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              
              <div className="flex-shrink-0">
                {getStatusBadge(booking.status)}
              </div>
            </div>

            {/* Time Slots */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {booking.status === 'confirmed' ? 'Confirmed Time Slot:' : 'Proposed Time Slots:'}
              </h4>
              
              {booking.status === 'confirmed' && booking.selectedSlot ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center text-green-800">
                    <Calendar size={16} className="mr-2 flex-shrink-0" />
                    <span className="font-medium text-sm">
                      {formatDateTime(booking.selectedSlot.date, booking.selectedSlot.time)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {booking.timeSlots?.map((slot, index) => (
                    <div 
                      key={index} 
                      className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex items-center text-gray-700">
                        <Calendar size={14} className="mr-2 flex-shrink-0" />
                        <span className="text-xs sm:text-sm">
                          Option {index + 1}: {formatDateTime(slot.date, slot.time)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Message */}
            {booking.message && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Your Message:</h4>
                <p className="text-xs sm:text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {booking.message}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t">
              <button
                onClick={() => onSelectTeacher(booking.teacherId)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-md hover:bg-blue-700 transition-colors"
              >
                View Teacher Profile
              </button>
              {booking.status === 'confirmed' && booking.teacher?.phone && (
                <button
                  onClick={() => {
                    toast.success('Contact teacher at: ' + booking.teacher.phone);
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white text-xs sm:text-sm rounded-md hover:bg-green-700 transition-colors"
                >
                  Contact Teacher
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// My Teachers Tab Component
const MyTeachersTab = ({ onSelectBatch, onSelectTeacher }) => {
  const { currentUser: user } = useRealtime();
  const [hiredTeachers, setHiredTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load the user's hired teachers
  const loadHiredTeachers = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const teachers = await teacherHiringService.getStudentHiredTeachers(user.uid);
      
      // Get batch information for each teacher
      const teachersWithBatches = [];
      for (const teacher of teachers) {
        try {
          // Find the batch for this teacher-student combination
          const studentBatches = await teacherHiringService.getStudentBatches(user.uid);
          const teacherBatch = studentBatches.find(batch => batch.teacherId === teacher.id);
          
          teachersWithBatches.push({
            ...teacher,
            batchId: teacherBatch?.id || null
          });
        } catch (error) {
          teachersWithBatches.push({
            ...teacher,
            batchId: null
          });
        }
      }
      
      setHiredTeachers(teachersWithBatches);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHiredTeachers();
  }, [user]);

  if (loading) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-3 sm:mb-4"></div>
        <p className="text-sm sm:text-base text-gray-600">Loading your teachers...</p>
      </div>
    );
  }

  if (hiredTeachers.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <Users size={48} className="sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
        <h3 className="text-base sm:text-xl font-semibold text-gray-900 mb-2">No teachers hired yet</h3>
        <p className="text-xs sm:text-base text-gray-600 mb-4 px-4">
          Start by searching for teachers and booking demo classes
        </p>
        <button
          onClick={() => {/* Switch to search tab */}}
          className="px-4 sm:px-6 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 transition-colors"
        >
          Find Teachers
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">My Teachers</h2>
        <button
          onClick={loadHiredTeachers}
          disabled={loading}
          className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {hiredTeachers.map((teacher) => (
          <div key={teacher.id} className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
            <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4">
              {teacher.photoUrl ? (
                <img
                  src={teacher.photoUrl}
                  alt={teacher.name}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={20} className="sm:w-6 sm:h-6 text-gray-400" />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{teacher.name}</h3>
                <div className="flex items-center">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        className={`sm:w-4 sm:h-4 ${i < Math.floor(teacher.rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <span className="ml-1 text-xs sm:text-sm text-gray-600">({teacher.rating || 0})</span>
                </div>
              </div>
            </div>

            <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <MapPin size={12} className="sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="truncate">{[teacher.locality, teacher.city, teacher.state].filter(Boolean).join(', ')}</span>
              </div>
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <span className="mr-1.5 sm:mr-2 text-xs sm:text-sm font-semibold">₹</span>
                <span>{teacher.feesPerHour}/hour</span>
              </div>
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <Clock size={12} className="sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span>{teacher.experience} years experience</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-1 mb-3 sm:mb-4">
              {teacher.subjects?.slice(0, 3).map((subject) => (
                <span
                  key={subject}
                  className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-[10px] sm:text-xs rounded-full"
                >
                  {subject}
                </span>
              ))}
              {teacher.subjects?.length > 3 && (
                <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 text-gray-600 text-[10px] sm:text-xs rounded-full">
                  +{teacher.subjects.length - 3} more
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
              <button 
                onClick={() => onSelectTeacher(teacher.id)}
                className="flex-1 px-3 py-2 bg-gray-600 text-white text-xs sm:text-sm rounded-md hover:bg-gray-700 transition-colors"
              >
                View Profile
              </button>
              {teacher.batchId && (
                <button 
                  onClick={() => onSelectBatch(teacher.batchId)}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-md hover:bg-blue-700 transition-colors"
                >
                  View Batch
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// My Batches Tab Component
const MyBatchesTab = ({ onSelectBatch }) => {
  const { currentUser: user } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load the user's batches with teacher details
  const loadBatches = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const studentBatches = await teacherHiringService.getStudentBatches(user.uid);
      
      // Fetch teacher details for each batch
      const batchesWithTeachers = [];
      for (const batch of studentBatches) {
        try {
          const teacher = await tuitionTeacherService.getTeacher(batch.teacherId);
          batchesWithTeachers.push({
            ...batch,
            teacher: teacher
          });
        } catch (error) {
          // Still add the batch even if teacher fetch fails
          batchesWithTeachers.push({
            ...batch,
            teacher: null
          });
        }
      }
      
      setBatches(batchesWithTeachers);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBatches();
  }, [user]);

  if (loading) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-3 sm:mb-4"></div>
        <p className="text-sm sm:text-base text-gray-600">Loading your batches...</p>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <BookOpen size={48} className="sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
        <h3 className="text-base sm:text-xl font-semibold text-gray-900 mb-2">No batches yet</h3>
        <p className="text-xs sm:text-base text-gray-600 mb-4 px-4">
          Hire a teacher to start your learning journey
        </p>
        <button
          onClick={() => {/* Switch to search tab */}}
          className="px-4 sm:px-6 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 transition-colors"
        >
          Find Teachers
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">My Batches</h2>
        <button
          onClick={loadBatches}
          disabled={loading}
          className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {batches.map((batch) => (
          <div key={batch.id} className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate flex-1 mr-2">{batch.batchName}</h3>
              <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-100 text-green-800 text-[10px] sm:text-xs rounded-full whitespace-nowrap flex-shrink-0">
                Active
              </span>
            </div>

            {/* Teacher Info */}
            {batch.teacher && (
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 rounded-lg">
                {batch.teacher.photoUrl ? (
                  <img
                    src={batch.teacher.photoUrl}
                    alt={batch.teacher.name}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <GraduationCap size={18} className="sm:w-5 sm:h-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm sm:text-base text-gray-900 truncate">{batch.teacher.name}</h4>
                  <div className="flex items-center">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={10}
                          className={`sm:w-3 sm:h-3 ${i < Math.floor(batch.teacher.rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <span className="ml-1 text-[10px] sm:text-xs text-gray-600">({batch.teacher.rating || 0})</span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <GraduationCap size={12} className="sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="truncate">{batch.teacher ? batch.teacher.name : `Teacher ID: ${batch.teacherId}`}</span>
              </div>
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <BookOpen size={12} className="sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="truncate">Batch: {batch.batchName}</span>
              </div>
              {batch.teacher && batch.teacher.subjects && (
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <Users size={12} className="sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">Subjects: {batch.teacher.subjects.slice(0, 2).join(', ')}
                  {batch.teacher.subjects.length > 2 && ` +${batch.teacher.subjects.length - 2} more`}</span>
                </div>
              )}
              <div className="flex items-center text-xs sm:text-sm text-gray-600">
                <Clock size={12} className="sm:w-3.5 sm:h-3.5 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span>Created: {batch.createdAt ? new Date(batch.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => onSelectBatch(batch.id)}
                className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-md hover:bg-blue-700 transition-colors"
              >
                Open Batch
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TuitionTeachers;
